import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Zap,
  MapPin,
  Car,
  Clock,
  Eye,
  TrendingUp,
  TrendingDown,
  BarChart3,
  FileText,
  Activity,
  Calendar,
  DollarSign,
} from "lucide-react";
import DataTablePagination from "@/components/ui/data-table-pagination";
import {
  useSurgePricings,
  useDeleteSurgePricing,
  useToggleSurgePricingStatus,
} from "@/api-services/hooks/useSurgePricing";
import { useCities } from "@/api-services/hooks/usePricing";
import {
  SurgePricing,
  StatusOption,
  DayOfWeekOption,
} from "@/api-services/hooks/surgePricingTypes";
import DeleteSurgePricingConfirmation from "@/components/surge-pricing/DeleteSurgePricingConfirmation";
import { CityMaster } from "@/api-services/hooks/pricingTypes";

const SurgePricingPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusOption>("all");
  const [dayFilter, setDayFilter] = useState<DayOfWeekOption>("all");

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete functionality state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [surgePricingToDelete, setSurgePricingToDelete] = useState<{
    id: number;
    details: string;
  } | null>(null);

  
  // Fetch cities data for lookup
  const { data: cities } = useCities({ enabled: true });

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Handle search input change with useCallback to prevent re-creation
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Determine if we need client-side filtering
  const hasClientSideFilters = statusFilter !== "all" || dayFilter !== "all";
  
  // When client-side filters are active, fetch a large page size to have enough data to filter
  // Otherwise, use normal pagination
  const apiPageSize = hasClientSideFilters ? 1000 : itemsPerPage;
  const apiPage = hasClientSideFilters ? 0 : currentPage; // Always fetch from page 0 when filtering client-side

  // Reset to first page when search filter changes (client-side filters don't reset page)
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm]);

  // Fetch surge pricings from API (only search filter goes to API, status and day are client-side)
  const { data, isLoading: loading, error, refetch } = useSurgePricings(
    {
      page: apiPage,
      size: apiPageSize,
      ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
    },
    {
      onError: (err) => {
        toast.error(`Failed to load surge pricing: ${err.message}`);
      },
    }
  );

  // Delete surge pricing hook
  const { mutate: deleteSurgePricing, isPending: isDeleting } =
    useDeleteSurgePricing({
      onSuccess: async (data, surgePricingId) => {
        toast.success("Surge pricing rule deleted successfully!");
        // Invalidate all surge pricings queries to ensure fresh data
        await queryClient.invalidateQueries({ queryKey: ['surge-pricings'] });
      },
      onError: (error, surgePricingId) => {
        toast.error(`Failed to delete surge pricing: ${error.message}`);
      },
    });

  // Toggle surge pricing status hook
  const { mutate: toggleStatus, isPending: isToggling } =
    useToggleSurgePricingStatus({
      onSuccess: async (data, surgePricingId) => {
        toast.success("Surge pricing status updated successfully!");
        // Update only the changed row in the cache instead of refetching entire table
        queryClient.setQueriesData(
          { queryKey: ['surge-pricings'] },
          (oldData: any) => {
            if (!oldData?.content) return oldData;

            const updatedContent = oldData.content.map((surgePricing: any) =>
              surgePricing.surgeRuleId === surgePricingId
                ? { ...surgePricing, isActive: !surgePricing.isActive }
                : surgePricing
            );

            return {
              ...oldData,
              content: updatedContent,
            };
          }
        );
      },
      onError: (error, surgePricingId) => {
        toast.error(`Failed to update surge pricing status: ${error.message}`);
      },
    });

  const getStatusBadge = useCallback((isActive: boolean) => {
    return isActive
      ? {
          variant: "default",
          className: "bg-success text-success-foreground",
          label: "Active",
        }
      : {
          variant: "secondary",
          className: "bg-muted text-muted-foreground",
          label: "Inactive",
        };
  }, []);

  const handleSurgePricingAction = (
    action: string,
    surgePricingId: number,
    surgePricingDetails: string
  ) => {
    if (action === "Edit") {
      navigate(`/admin/surge-pricing/edit/${surgePricingId}`);
    } else if (action === "View") {
      navigate(`/admin/surge-pricing/view/${surgePricingId}`);
    } else if (action === "Toggle Status") {
      toggleStatus(surgePricingId);
    }
  };

  // Delete handler functions
  const handleDeleteClick = (
    surgePricingId: number,
    surgePricingDetails: string
  ) => {
    setSurgePricingToDelete({
      id: surgePricingId,
      details: surgePricingDetails,
    });
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = (surgePricingId: number) => {
    deleteSurgePricing(surgePricingId);
    setDeleteConfirmationOpen(false);
    setSurgePricingToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setSurgePricingToDelete(null);
  };

  // Filter surge pricings by status and day on the client side
  const surgePricings = useMemo(() => {
    const surgePricingData = data as any;
    if (!surgePricingData || !surgePricingData.content) return [];

    let filtered = surgePricingData.content;

    // Filter by status (active/inactive)
    if (statusFilter !== "all") {
      filtered = filtered.filter((surgePricing) => {
        if (statusFilter === "active") {
          return surgePricing.isActive === true;
        } else if (statusFilter === "inactive") {
          return surgePricing.isActive === false;
        }
        return true;
      });
    }

    // Filter by day of week
    if (dayFilter !== "all") {
      const dayFilterUpper = dayFilter.toUpperCase();
      filtered = filtered.filter((surgePricing) => {
        return surgePricing.dayOfWeek?.toUpperCase() === dayFilterUpper;
      });
    }

    return filtered;
  }, [data, statusFilter, dayFilter, hasClientSideFilters]);

  // Pagination calculations - use filtered count for client-side filters
  const totalFilteredItems = surgePricings.length;
  const surgePricingData = data as any;
  const totalItems = surgePricingData?.page?.totalElements ?? 0;
  
  // Calculate total pages based on filtered items
  const totalPages = useMemo(() => {
    if (statusFilter !== "all" || dayFilter !== "all") {
      // Client-side filtering: paginate filtered results
      return Math.ceil(totalFilteredItems / itemsPerPage);
    }
    // Server-side pagination: use API response
    return surgePricingData?.page?.totalPages || 0;
  }, [totalFilteredItems, itemsPerPage, statusFilter, dayFilter, surgePricingData?.page?.totalPages]);

  // Paginated surge pricings for current page (when using client-side filtering)
  const paginatedSurgePricings = useMemo(() => {
    if (statusFilter === "all" && dayFilter === "all") {
      // No client-side filtering, use API pagination
      return surgePricings;
    }
    // Client-side pagination for filtered results
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return surgePricings.slice(startIndex, endIndex);
  }, [surgePricings, currentPage, itemsPerPage, statusFilter, dayFilter]);

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  // Check if current page is empty after data loads and navigate to previous page if needed
  useEffect(() => {
    if (!loading && data && paginatedSurgePricings.length === 0 && currentPage > 0 && totalPages > 0) {
      // Current page is empty, navigate to the last valid page
      const lastPage = Math.max(0, totalPages - 1);
      setCurrentPage(lastPage);
    }
  }, [loading, paginatedSurgePricings.length, currentPage, totalPages, data]);

  // Format surge pricing details for display
  const getSurgePricingDetails = useCallback((surgePricing: SurgePricing) => {
    const cityName = cities?.find((city: CityMaster) => city.cityId === surgePricing.city?.cityId)?.cityName || "Unknown City";
    return `${cityName} - ${
      surgePricing.vehicleCategoryId
        ? "Vehicle " + surgePricing.vehicleCategoryId
        : "Unknown Vehicle"
    } - ${surgePricing.dayOfWeek}`;
  }, [cities]);

  // Format time from LocalTime object or string
  const formatTime = useCallback(
    (time: { hour: number; minute: number } | string) => {
      if (typeof time === "string") {
        // If time is already a string (e.g., "18:00:00"), format it
        const [hours, minutes] = time.split(":");
        return `${hours}:${minutes}`;
      } else if (
        time &&
        typeof time === "object" &&
        "hour" in time &&
        "minute" in time
      ) {
        // If time is an object with hour and minute properties
        const hour = time.hour.toString().padStart(2, "0");
        const minute = time.minute.toString().padStart(2, "0");
        return `${hour}:${minute}`;
      }
      // Fallback return
      return "00:00";
    },
    []
  );

  // Format currency display
  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  }, []);

  // Get day display name
  const getDayDisplayName = useCallback((day: string) => {
    const days: Record<string, string> = {
      MONDAY: "Monday",
      TUESDAY: "Tuesday",
      WEDNESDAY: "Wednesday",
      THURSDAY: "Thursday",
      FRIDAY: "Friday",
      SATURDAY: "Saturday",
      SUNDAY: "Sunday",
    };
    return days[day] || day;
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading surge pricing data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <TrendingDown className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Surge Pricing
            </h3>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Surge Pricing Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage and configure surge pricing rules for peak hours and
            high-demand periods
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            className="bg-gradient-primary hover:opacity-90"
            onClick={() => navigate("/admin/surge-pricing/add")}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Surge Rule
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Search surge pricing by city or vehicle..."
                className="pl-10"
                value={searchTerm}
                onChange={handleSearchChange}
                autoComplete="off"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value: StatusOption) => setStatusFilter(value)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={dayFilter}
              onValueChange={(value: DayOfWeekOption) => setDayFilter(value)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Days" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Days</SelectItem>
                <SelectItem value="monday">Monday</SelectItem>
                <SelectItem value="tuesday">Tuesday</SelectItem>
                <SelectItem value="wednesday">Wednesday</SelectItem>
                <SelectItem value="thursday">Thursday</SelectItem>
                <SelectItem value="friday">Friday</SelectItem>
                <SelectItem value="saturday">Saturday</SelectItem>
                <SelectItem value="sunday">Sunday</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Surge Pricing Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Surge Pricing Rules (
            {statusFilter !== "all" || dayFilter !== "all" 
              ? `${totalFilteredItems} filtered` 
              : totalItems}
            )
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location & Vehicle</TableHead>
                  <TableHead>Day</TableHead>
                  <TableHead>Time Range</TableHead>
                  <TableHead>Multiplier</TableHead>
                  <TableHead>Max Cap</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedSurgePricings.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {surgePricings.length === 0 
                        ? "No surge pricing rules found"
                        : "No surge pricing rules match the current filters"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedSurgePricings.map((surgePricing) => {
                    const statusBadge = getStatusBadge(surgePricing.isActive);
                    const surgePricingDetails =
                      getSurgePricingDetails(surgePricing);

                    return (
                      <TableRow
                        key={surgePricing.surgeRuleId}
                        className="hover:bg-muted/50"
                      >
                        <TableCell>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">
                                {cities?.find(city => city.cityId === surgePricing.city?.cityId)?.cityName || "Unknown City"}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Car className="w-3 h-3" />
                              <span>
                                Vehicle ID: {surgePricing.vehicleCategoryId}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">
                              {getDayDisplayName(surgePricing.dayOfWeek)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span>
                              {formatTime(surgePricing.startTime)} -{" "}
                              {formatTime(surgePricing.endTime)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-bold text-orange-600">
                            {surgePricing.surgeMultiplier.toFixed(1)}x
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-red-600 font-semibold">
                            {formatCurrency(surgePricing.maxSurgeCap)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge.className}>
                            {surgePricing.isActive ? (
                              <>
                                <ToggleRight className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </>
                            ) : (
                              <>
                                <ToggleLeft className="w-3 h-3 mr-1" />
                                {statusBadge.label}
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={() => {
                                  navigate(
                                    `/admin/surge-pricing/view/${
                                      surgePricing.surgeRuleId || 0
                                    }`
                                  );
                                }}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  handleSurgePricingAction(
                                    "Edit",
                                    surgePricing.surgeRuleId || 0,
                                    surgePricingDetails
                                  )
                                }
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Surge Rule
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  handleSurgePricingAction(
                                    "Toggle Status",
                                    surgePricing.surgeRuleId || 0,
                                    surgePricingDetails
                                  )
                                }
                                disabled={isToggling}
                              >
                                {surgePricing.isActive ? (
                                  <>
                                    <ToggleLeft className="w-4 h-4 mr-2" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <ToggleRight className="w-4 h-4 mr-2" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  handleDeleteClick(
                                    surgePricing.surgeRuleId || 0,
                                    surgePricingDetails
                                  )
                                }
                                className="text-destructive"
                                disabled={isDeleting}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Surge Rule
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination - Always show if totalPages > 0, even if current page is empty */}
            {totalPages > 0 && (
              <DataTablePagination
                currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
                totalPages={totalPages}
                totalItems={statusFilter !== "all" || dayFilter !== "all" ? totalFilteredItems : totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Surge Pricing Confirmation Modal */}
      <DeleteSurgePricingConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        surgePricingDetails={surgePricingToDelete?.details || ""}
        surgePricingId={surgePricingToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default SurgePricingPage;
